import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Anchor,
  Box,
  Button,
  Group,
  Stack,
  Text,
  TextInput,
} from "@mantine/core";
import { IconSearch } from "@tabler/icons-react";
import { useSalon } from "../../../context/SalonContext";
import { SalonService } from "../../../types/salon";
import { ServiceCommissionOverride } from "../../../types/commission";
import {
  ServiceOverrideDrafts,
  describeServiceOverrides,
  draftsToOverrides,
} from "../../../utils/serviceCommissionOverrides";
import CommissionInput from "../CommissionInput";
import { CommissionFormFrame, inlineFormFrame } from "./commissionFormFrame";
import {
  formatServiceMeta,
  groupByCategory,
  servicesFor,
} from "./serviceRateMenu";
import styles from "./ServiceSpecificRatesScreen.module.css";

export interface ServiceSpecificRatesScreenProps {
  /** The staff member being configured; "" for a tier template (every service). */
  staffId: string;
  value: ServiceOverrideDrafts;
  onChange: (next: ServiceOverrideDrafts) => void;
  /** Validated: go back to the form in service-specific mode. */
  onDone: () => void;
  /** Put this rate in every empty box when the screen opens — the single
   * rate being replaced, or a legacy record's non-zero default (this screen
   * saves the default as 0%, so pre-filling keeps pay unchanged). */
  fillEmptyWith?: string | null;
  /** The record's saved rates — listed when the service isn't offered now. */
  savedOverrides?: ServiceCommissionOverride[];
  onServiceNames?: (names: Record<string, string>) => void;
  disabled?: boolean;
  frame?: CommissionFormFrame;
}

/** Put `rate` in every offered service's box that is still empty. */
const fillEmpty = (
  drafts: ServiceOverrideDrafts,
  offered: SalonService[],
  rate: string,
): ServiceOverrideDrafts => {
  const next = { ...drafts };
  offered.forEach((service) => {
    if (!(next[service.id] || "").trim()) next[service.id] = rate;
  });
  return next;
};

const matches = (service: SalonService, needle: string): boolean =>
  !needle || service.name.toLowerCase().includes(needle);

/**
 * The commission form's "Rates by service" screen. One box per service the
 * staff member performs, grouped by category; a box left empty earns 0% (no
 * record is saved for it). Rates saved for services no longer offered stay
 * as they are and are listed below.
 */
const ServiceSpecificRatesScreen: React.FC<ServiceSpecificRatesScreenProps> = ({
  staffId,
  value,
  onChange,
  onDone,
  fillEmptyWith,
  savedOverrides,
  onServiceNames,
  disabled = false,
  frame = inlineFormFrame,
}) => {
  const { services } = useSalon() as { services: SalonService[] | null };
  const offered = useMemo(
    () => servicesFor(services || [], staffId),
    [services, staffId],
  );
  const [search, setSearch] = useState("");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    onServiceNames?.(Object.fromEntries(offered.map((s) => [s.id, s.name])));
  }, [offered, onServiceNames]);

  // Pre-fill empty boxes once, when the services load; later edits are the
  // user's.
  const prefilled = useRef(false);
  useEffect(() => {
    if (prefilled.current || !fillEmptyWith?.trim() || !services) return;
    prefilled.current = true;
    onChange(fillEmpty(value, offered, fillEmptyWith.trim()));
  }, [services, fillEmptyWith, offered, value, onChange]);

  const needle = search.trim().toLowerCase();
  const groups = groupByCategory(offered.filter((s) => matches(s, needle)));
  const offeredIds = new Set(offered.map((s) => s.id));
  const kept = (savedOverrides || []).filter(
    (o) => !offeredIds.has(o.salon_service_id),
  );
  const anyFilled = offered.some((s) => (value[s.id] || "").trim());

  const done = (): void => {
    const result = draftsToOverrides(
      value,
      Object.fromEntries(offered.map((s) => [s.id, s.name])),
    );
    if (result.error) {
      setError(result.error);
      return;
    }
    onDone();
  };

  const clearAll = (): void => {
    setError(null);
    const next = { ...value };
    offered.forEach((service) => delete next[service.id]);
    onChange(next);
  };

  const toolbar = (
    <Stack gap={6}>
      <TextInput
        aria-label="Search services"
        placeholder="Search services"
        leftSection={<IconSearch size={14} />}
        value={search}
        onChange={(e) => setSearch(e.currentTarget.value)}
      />
      <Text size="xs" c="dimmed">
        Enter percentages/fixed amounts for each service. Empty services earn no
        commission.
      </Text>
    </Stack>
  );

  return frame({
    screen: "service-rates",
    title: "Rates by service",
    onBack: done,
    toolbar,
    footer: (
      <Group justify="space-between" wrap="nowrap" gap="sm">
        <Text size="sm" c="red">
          {error}
        </Text>
        <Button color="teal" onClick={done} disabled={disabled}>
          Done
        </Button>
      </Group>
    ),
    children: (
      <Stack gap={0}>
        {anyFilled && (
          <Group justify="flex-end">
            <Anchor
              component="button"
              type="button"
              size="xs"
              onClick={clearAll}
              disabled={disabled}
            >
              Clear all
            </Anchor>
          </Group>
        )}
        {groups.length === 0 && (
          <Text size="sm" c="dimmed" ta="center" py="md">
            {offered.length === 0
              ? "This staff member doesn't perform any services yet."
              : `No services match "${search.trim()}"`}
          </Text>
        )}
        {groups.map(({ category, items }) => (
          <Box key={category}>
            <Text className={styles.groupLabel}>{category}</Text>
            {items.map((service) => (
              <Group
                key={service.id}
                justify="space-between"
                wrap="nowrap"
                gap="sm"
                className={styles.row}
              >
                <Box className={styles.serviceText}>
                  <Text size="sm" fw={500} truncate>
                    {service.name}
                  </Text>
                  <Text size="xs" c="dimmed" truncate>
                    {formatServiceMeta(service, staffId)}
                  </Text>
                </Box>
                <Box className={styles.rateBox}>
                  <CommissionInput
                    ariaLabel={`Commission rate for ${service.name}`}
                    placeholder="0%"
                    value={value[service.id] ?? ""}
                    onChange={(rate) => {
                      setError(null);
                      onChange({ ...value, [service.id]: rate });
                    }}
                    disabled={disabled}
                  />
                </Box>
              </Group>
            ))}
          </Box>
        ))}
        {kept.length > 0 && (
          <Text size="xs" c="dimmed" mt="sm">
            Kept for services not offered now:{" "}
            {describeServiceOverrides(kept).join(", ")}
          </Text>
        )}
      </Stack>
    ),
  });
};

export default ServiceSpecificRatesScreen;
